import React from 'react';
import { Link } from 'react-router-dom';
import PageHero from '../components/PageHero';
import Status from '../components/Status';
import SourceLink from '../components/SourceLink';
import { SITE, SOURCES } from '../content/site';

const Forecaster: React.FC = () => (
  <div>
    <PageHero
      title="Open Day-Ahead Forecaster"
      lead="A day-ahead load forecaster built only from public data, run unchanged on 43 U.S. balancing authorities and scored against each one's own published forecast. It shows how far public data alone can go, and where it still falls short."
    >
      <div className="flex flex-wrap items-center gap-3">
        <Status tone="live">v1.0.0 released · October 2026</Status>
        <Status tone="neutral">Apache-2.0</Status>
        <Status tone="neutral">Python</Status>
      </div>
    </PageHero>

    <section className="bg-white section-padding">
      <div className="container-width">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <h2 className="text-3xl font-poppins font-bold text-dark-gray mb-6">How it forecasts</h2>
            <div className="space-y-6">
              <Method tag="DATA" color="bg-primary-red" title="Public inputs, read at 9 a.m. of the day before">
                Hourly demand from Form EIA-930, and the temperature forecast at one to six airports per balancing
                authority by three NOAA models (HRRR, the National Blend of Models and GFS), from the runs available at
                the cut-off. Each day is forecast with only what was known then.
              </Method>
              <Method tag="MODEL" color="bg-light-blue" title="Three members, weighted by their recent error">
                An additive regression per hour, gradient-boosted trees and a recency-effect regression, averaged by
                how well each did over the last 30 known days. The three weather models are averaged the same way, per
                station. The result is corrected by the forecaster's own errors on the 40 known days most like the
                target day.
              </Method>
              <Method tag="BAND" color="bg-dark-gray" title="A 90 % band that widens when the weather is uncertain">
                An adaptive interval from the forecaster's recent errors, stretched or narrowed by how much the members
                of the National Blend of Models disagree about the day. It is what keeps the band honest on the days of
                winter storms.
              </Method>
            </div>
            <p className="text-gray-600 text-sm mt-8">
              The recipe was fixed on eleven large balancing authorities over 2020 to 2022, then run once, unchanged,
              on every balancing authority with weather stations in its map: 43 of them, 2023 to 2025. Every rule,
              every parameter and every test that was not adopted is written down in the methods document.
            </p>
          </div>
          <div className="bg-gray-50 rounded-2xl p-8">
            <h3 className="text-2xl font-poppins font-semibold text-dark-gray mb-6">Results, 2023 to 2025</h3>
            <ul className="space-y-3 text-gray-700 text-sm">
              <li>
                • On the 38 balancing authorities whose published forecast is comparable with their demand: mean daily
                error 2.41 % against 3.16 % for their own forecasts, better in 26 of them. Source:{' '}
                <SourceLink source={SOURCES.forecasterScoreboard} />.
              </li>
              <li>
                • On the 27 it never saw during development: 3.18 % against 4.14 %. Source:{' '}
                <SourceLink source={SOURCES.forecasterScoreboard} />.
              </li>
              <li>
                • Most of that margin is a definition gap: many published forecasts sit a fixed amount away from the
                demand at each hour. With that offset removed they reach 2.19 %, ahead of the open forecaster. Source:{' '}
                <SourceLink source={SOURCES.forecasterOffsets} />.
              </li>
              <li>
                • At the peak of the extreme days the operators are ahead: on the days of the winter storms of 2024 and
                2025 the open forecaster under-forecasts the daily peak by 2.43 % on average, against 2.33 %. Source:{' '}
                <SourceLink source={SOURCES.forecasterStrata} />.
              </li>
              <li>
                • The 90 % band covers 89.2 % of the hours over the 43 balancing authorities, and 89.8 % on the days of
                the winter storms, against 77.6 % for a standard adaptive band. Sources:{' '}
                <SourceLink source={SOURCES.forecasterScoreboard} />, <SourceLink source={SOURCES.forecasterBands} />.
              </li>
              <li>
                • Timing is not the explanation: run at 9 p.m. instead of 9 a.m., the error on the eleven development
                balancing authorities falls by about a tenth of a point. Source:{' '}
                <SourceLink source={SOURCES.forecasterCutoff} />.
              </li>
            </ul>
            <h3 className="text-2xl font-poppins font-semibold text-dark-gray mt-8 mb-4">Limitations</h3>
            <ul className="space-y-2 text-gray-700 text-sm">
              <li>• The operators forecast with information this study does not have; the 9 a.m. cut-off is a convention.</li>
              <li>• 32 of the 43 balancing authorities forecast from a single weather station.</li>
              <li>
                • On the eleven development balancing authorities, 2023 to 2025 had already been looked at during
                development, so their numbers are estimates.
              </li>
              <li>
                • The cold-day rules need 100 forecast days of the same run, so they are inactive at the start of every
                run; and the band's edges are scored as the 5th and 95th percentiles although they are read at an
                adaptive level. Both are measured and documented in the methods document.
              </li>
            </ul>
            <h3 className="text-2xl font-poppins font-semibold text-dark-gray mt-8 mb-4">Read more</h3>
            <ul className="space-y-2 text-gray-700 text-sm">
              <li>
                <Link to="/notes/open-forecaster-43-bas" className="text-primary-red hover:underline">
                  Note 3: the forecaster on 43 balancing authorities
                </Link>
              </li>
              <li>
                <Link to="/notes/prediction-intervals-winter-storms" className="text-primary-red hover:underline">
                  Note 4: prediction intervals that hold in winter storms
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <section className="gradient-bg text-white section-padding">
      <div className="container-width text-center">
        <h2 className="text-3xl font-poppins font-bold mb-4">Run, cite, contribute</h2>
        <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
          The repository has the forecaster, the data pipeline, a step-by-step reproduction guide and the report behind
          every number. Release 1.0.0 is archived on Zenodo as DOI 10.5281/zenodo.23327586; the citation record is in
          CITATION.cff.
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <a href={`${SITE.github}/ba-load-forecast`} target="_blank" rel="noopener noreferrer" className="btn-primary">
            Repository on GitHub
          </a>
          <a
            href="https://doi.org/10.5281/zenodo.23327586"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-outline-light"
          >
            DOI 10.5281/zenodo.23327586
          </a>
        </div>
      </div>
    </section>
  </div>
);

const Method: React.FC<{ tag: string; color: string; title: string; children: React.ReactNode }> = ({
  tag,
  color,
  title,
  children,
}) => (
  <div className="flex items-start space-x-4">
    <div className={`w-12 h-12 ${color} rounded-full flex items-center justify-center flex-shrink-0 mt-1`}>
      <span className="text-white text-[10px] font-bold">{tag}</span>
    </div>
    <div>
      <h4 className="font-poppins font-semibold text-dark-gray mb-2">{title}</h4>
      <p className="text-gray-600">{children}</p>
    </div>
  </div>
);

export default Forecaster;
